import * as React from "react";
import { vw } from "../../../lib/utils/responsiveUtils";
import { useQuery } from "@tanstack/react-query";
import type { getResveTimeDetailGolfParkForm } from "../../../@apis/dto/request/resve.request";
import { useGetResveTimeDetail } from "../../../@apis/hooks/useResve";
import { useFormContext } from "react-hook-form";
import { formatKoDayDate } from "../../../lib/utils/dateUtils";
import { staticURL } from "../../../lib/utils/imageUtils";
import type { ResveListItem } from "../../../@apis/dto/response/resve.response";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import { isAxiosError } from "axios";
import { useModalStore } from "../../../lib/store/useModalStore";
import type { Response } from "../../../@apis/dto/response/common.response";

type CourseScreenProps = {
  golfParkCode?: string;
  date?: string;
  prev?: () => void
};

type ReservationForm = {
  selectedCourse?: string;
  allAgreed?: boolean;
  memberCount: number;
  resveDetail: ResveListItem;
};

export default function MemberScreen({ golfParkCode, date, prev }: CourseScreenProps) {
  const { myInfo } = useMyInfoStore();
  const { watch, setValue } = useFormContext<ReservationForm>();
  const selectedCourse = watch("selectedCourse");

  const { openModal } = useModalStore();

  const propsForm: getResveTimeDetailGolfParkForm = {
    golfParkCode: golfParkCode ?? "",
    date: date ?? "",
    resveSeq: Number(selectedCourse || 0),
  };

  const { data: resveTimeDetailResponse, isSuccess , isError, error } = useQuery({
    ...useGetResveTimeDetail(propsForm),
    enabled: !!propsForm.golfParkCode && !!propsForm.date && !!selectedCourse,
    retry: false,
  });
  const timeDetailInfo = resveTimeDetailResponse?.data?.resveInfo;
  const resveTimeMemberList = timeDetailInfo?.resveTimeMemberList || [];

  // 동반자 선택된 칸 인덱스 (0~3)
  const [selectedAddIndexes, setSelectedAddIndexes] = React.useState<number[]>([]);
  const selectedMember = 1 + selectedAddIndexes.length;

  React.useEffect(() => {
    setValue("memberCount", selectedMember);
  }, [selectedMember, setValue]);

  React.useEffect(() => {
    if (isSuccess && timeDetailInfo) {
      setValue("resveDetail", timeDetailInfo as ResveListItem);
    }
  }, [isSuccess, setValue, timeDetailInfo])

  // React.useEffect(() => {
  //   if (!isError || !error) return;

  //   if (isAxiosError(error)) {
  //     const status = error.response?.status;

  //     if (status === 400) {
  //       openModal("ALERT_DIALOG", { title: "에러", content: "조회할 수 없는 예약 건입니다."})
  //       prev?.()
  //     }
  //   }
  // }, [isError, error, openModal, prev]);

  React.useEffect(() => {
    if (!isError || !error) return;

    if (isAxiosError(error)) {
      // 여기 안에 백엔드 바디가 들어있을 수 있음!
      const body = error.response?.data as Response<unknown> | undefined;

      openModal("ALERT_DIALOG", { title: "에러", content: body?.error?.message ?? error.message });
      prev?.();
      return;
    }

    openModal("ALERT_DIALOG", { title: "에러", content: String(error) });
    prev?.();
  }, [error, isError, openModal, prev, resveTimeDetailResponse]);

  return (
    <div className="px-[15px] flex flex-col max-h-screen overflow-y-auto">
      {/* 날짜 영역 */}
      <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(60) }}>
        <p className="font-bold text-[#222]" style={{ fontSize: vw(16) }}>
          {formatKoDayDate(date ?? "")} {timeDetailInfo?.startTime}
        </p>
      </div>

      <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px", }} />

      {/* 내용 영역 */}
      <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(124) }}>
        <div className="flex items-center gap-[19px]">
          <img
            src={
              timeDetailInfo?.golfParkImageUrl
                ? staticURL(timeDetailInfo?.golfParkImageUrl)
                : "/images/img-login-logo.webp"
            }
            alt="골프장 이미지"
            className="rounded-[10px] object-cover bg-white"
            style={{ width: vw(83), height: vw(83) }}
          />
          <div className="flex flex-col justify-center">
            <p className="text-[#222] font-bold" style={{ fontSize: vw(20) }}>
              {timeDetailInfo?.golfParkName}
            </p>
            <div className="mt-[18px] pl-[15px] border-l-[2px] border-[#e6e6e6]">
              <p className="text-[14px] text-[#333] font-[500]" style={{ fontSize: vw(14) }} >
                {timeDetailInfo?.golfCourseName}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 참가자 4칸 영역 */}
      <div className="mt-[15px] grid grid-cols-2 gap-[10px]">
        {(() => {
          let meUsed = false; // 내 정보가 한 번 들어갔는지 플래그

          return [0, 1, 2, 3].map((i) => {
            const m = resveTimeMemberList[i];
            const isReserved = !!m; // 이미 예약된 자리인지
            const isMe = !isReserved && !meUsed && myInfo; // 나
            const isAdd = !isReserved && !isMe; // 동반자 추가
            if (isMe) meUsed = true;

            const name = isReserved ? "플레이어" : isMe ? myInfo.name : "동반자 추가";
            const sex = isReserved ? m.sex : isMe ? Number(myInfo.gender) : undefined;
            const isSelected = !!isMe || selectedAddIndexes.includes(i); 

            const bg = sex === 0 ? "#f95e80" : sex === 1 ? "#25a1d5" : "#e6e6e6";
            const border = isSelected ? "#222" : "#e6e6e6";
            const bgColor = isReserved ? "#f5f5f5" : "#fff";
            const textColor = isReserved ? "#ccc" : "#222"; 
            const icon =
              isAdd && isSelected
                ? "/icons/ic-gender.webp"
                : sex === 0
                ? "/icons/ic-female.webp"
                : sex === 1
                ? "/icons/ic-male.webp"
                : isReserved
                ? "/icons/ic-gender.webp" 
                : "/icons/ic-rest.webp";
            const displayName = isAdd && isSelected ? "동반자" : name;

            return (
              <div
                key={i}
                onClick={() => { if (isAdd && !isSelected) setSelectedAddIndexes((prev) => [...prev, i]); }}
                className="relative flex flex-col items-center justify-center rounded-[15px] p-[20px] cursor-pointer"
                style={{ height: vw(153), border: isReserved ? "none" : `1px solid ${border}`, backgroundColor: bgColor }}
              >
                {isAdd && isSelected && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedAddIndexes((p) => p.filter((x) => x !== i)); }}
                    className="absolute top-[10px] right-[10px] bg-[#888] rounded-full flex items-center justify-center"
                    style={{ width: vw(30), height: vw(30) }}
                  >
                    <img src="/icons/ic-close-white.webp" style={{ width: vw(10), height: vw(10) }} />
                  </button>
                )}
                <div className="rounded-full flex justify-center items-center" style={{ width: vw(80), aspectRatio: "1 / 1", backgroundColor: bg }} >
                  <img
                    src={icon}
                    style={{ width: vw(52), height: vw(44) }}
                  />
                </div>
                <p className="mt-[15px] font-bold" style={{ fontSize: vw(20), color: textColor }}>
                  {displayName}
                </p>
              </div>
            );
          });
        })()}
      </div>

      {/* 라운드 비용 */}
      <div className="mt-[15px]">
        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center justify-between" style={{ height: vw(58) }}>
          <p className="text-[#222] font-bold" style={{ fontSize: vw(16) }}>
            라운드 비용
          </p>
          <p className="text-[#e5002b] font-bold" style={{ fontSize: vw(20) }}>
            {(selectedMember)}인{" "}
            {(selectedMember * (timeDetailInfo?.greenfee ?? 0)).toLocaleString()}원
          </p>
        </div>
      </div>
    </div>
  );
}
